import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, keys, useMe } from '../../api.ts';
import { formatDate } from '../../../shared/format.ts';
import type { CompetitionSummary } from '../../../shared/types.ts';
import { ConfirmButton, ErrorBox, Loading, PageHeader, useDocumentTitle, useToast } from '../../components/ui.tsx';

export function CompetitionsPage() {
  useDocumentTitle('Kilpailut');
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: keys.competitions, queryFn: () => api.get<CompetitionSummary[]>('/competitions') });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/competitions/${id}`),
    onSuccess: () => {
      toast.ok('Kilpailu poistettu');
      void qc.invalidateQueries({ queryKey: keys.competitions });
    },
    onError: toast.error,
  });

  return (
    <div className="page">
      <PageHeader
        title="Kilpailut"
        sub="Aikarajatut DXCC-kilpailut: osallistu lataamalla kilpailuajan logi ADIF-tiedostona."
        actions={
          me?.isAdmin && (
            <Link className="btn btn-primary" to="/competitions/new">
              + Uusi kilpailu
            </Link>
          )
        }
      />
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : !q.data?.length ? (
        <div className="empty card">Kilpailuja ei ole vielä järjestetty.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Kilpailu</th>
                <th>Voimassa</th>
                <th>Tila</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/competitions/${c.id}`}>{c.heading}</Link>
                  </td>
                  <td className="mono">
                    {formatDate(c.startDate)} – {formatDate(c.endDate)}
                  </td>
                  <td>{c.isOpen ? <span className="badge badge-accent">Käynnissä</span> : <span className="badge">Päättynyt / tulossa</span>}</td>
                  <td>
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      {me && c.isOpen && (
                        <Link className="btn btn-sm btn-primary" to={`/competitions/${c.id}/participate`}>
                          Osallistu
                        </Link>
                      )}
                      {me?.isAdmin && (
                        <>
                          <Link className="btn btn-sm" to={`/competitions/${c.id}/edit`}>
                            Muokkaa
                          </Link>
                          <ConfirmButton
                            className="btn btn-sm btn-danger"
                            title="Poista kilpailu"
                            message={`Poistetaanko "${c.heading}" ja kaikki sen tulokset?`}
                            confirmText="Poista"
                            onConfirm={() => remove.mutateAsync(c.id)}
                          >
                            Poista
                          </ConfirmButton>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
